import { PublicTheme, ResponsivePaddingProps, ResponsiveHidingProps } from '~/design';
import { BackgroundProvider } from '~/internal';
import React, { forwardRef } from 'react';
import { assert } from '~/utils';
import { useTheme } from '@emotion/react';
import { useResponsivePaddingStyle, useResponsiveHideStyle } from '~/utils/styling';

const ALLOWED_COLORS = [
  'actionPrimary',
  'actionSecondary',
  'actionPositive',
  'actionWarning',
  'actionNegative',
  'actionMagic',
  'bgRegular',
] as const satisfies ReadonlyArray<keyof PublicTheme['colors']>;

export interface BackgroundProps
  extends React.HTMLAttributes<HTMLElement>,
    ResponsivePaddingProps,
    ResponsiveHidingProps {
  as?: React.ElementType;
  background: (typeof ALLOWED_COLORS)[number];
}

/**
 * A component that renders a background color.  This also sets the background
 * in the context, so children crystal elements may change color for improved
 * contrast
 */
export const Background = forwardRef(
  (
    {
      as: Component = 'div',
      children,
      background,
      hideAbove,
      hideBelow,
      p,
      pb,
      pl,
      pr,
      pt,
      px,
      py,
      ...props
    }: BackgroundProps,
    ref,
  ) => {
    const theme = useTheme();
    assert(background, '[Background] Background prop is required');
    assert(
      ALLOWED_COLORS.includes(background),
      `[Background] Invalid background color: ${background}`,
    );

    const backgroundColor = theme.colors[background];
    const paddingStyle = useResponsivePaddingStyle({
      p,
      pb,
      pl,
      pr,
      pt,
      px,
      py,
    });
    const hideStyle = useResponsiveHideStyle({ hideBelow, hideAbove });

    return (
      <Component
        ref={ref}
        {...props}
        style={{
          ...props.style,
          backgroundColor,
        }}
        css={[paddingStyle, hideStyle]}
      >
        <BackgroundProvider backgroundColor={backgroundColor}>{children}</BackgroundProvider>
      </Component>
    );
  },
);

Background.displayName = 'Background';
